import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, NavLink, Outlet, ScrollRestoration, useLocation } from "react-router";
import { api, call } from "../lib/api.ts";
import { useCartCount } from "../lib/cart.ts";
import { reopenConsent } from "../lib/consent.ts";
import { initPixel, pixel } from "../lib/pixel.ts";
import { toasts } from "../lib/toast.ts";
import { ConfirmHost } from "./ConfirmDialog.tsx";
import { CookieBanner } from "./CookieBanner.tsx";
import { Icon } from "./Icon.tsx";
import { Logo } from "./Logo.tsx";

export function useConfig() {
  return useQuery({
    queryKey: ["config"],
    queryFn: () => call(api.config.$get()),
    staleTime: 5 * 60_000,
  });
}

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => call(api.auth.me.$get()), staleTime: 60_000 });
}

function Header() {
  const count = useCartCount();
  const me = useMe();
  return (
    <header className="topstrip">
      <div className="topstrip__inner">
        <Link to="/" className="wordmark" aria-label="memeup home">
          <Logo title="memeup" />
        </Link>
        <nav aria-label="Main" className="topstrip__nav">
          <NavLink to="/shop">Shop</NavLink>
          <NavLink to="/make">Make yours</NavLink>
        </nav>
        <div className="topstrip__end">
          <NavLink
            to="/account"
            className="topstrip__account"
            aria-label={me.data?.user ? "Your account" : "Sign in"}
          >
            <Icon name="user" size={22} />
          </NavLink>
          <Link
            to="/bag"
            className="bagtab"
            data-bag-target
            aria-label={`Bag, ${count} item${count === 1 ? "" : "s"}`}
          >
            <Icon name="bag" size={20} />
            <span className="bagtab__label">Bag</span>
            <span className="bagtab__count tabular" data-empty={count === 0}>
              {count}
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function TestModeBanner() {
  const cfg = useConfig();
  if (!cfg.data) return null;
  const { paypal, gelatoMode } = cfg.data;
  if (paypal.mode === "live" && gelatoMode === "live") return null;
  const parts = [
    paypal.mode !== "live"
      ? paypal.mode === "mock"
        ? "payments simulated"
        : "PayPal sandbox"
      : null,
    gelatoMode !== "live" ? (gelatoMode === "mock" ? "printing simulated" : "Gelato drafts") : null,
  ].filter(Boolean);
  return (
    <div className="testmode" role="note">
      Test mode: {parts.join(" · ")}. No real orders.
    </div>
  );
}

function Footer() {
  const cfg = useConfig();
  return (
    <footer className="sheetfoot">
      <div className="sheetfoot__inner">
        <div className="sheetfoot__brand">
          <Logo className="wordmark wordmark--sm" />
          <p>Custom meme merch, printed on demand and shipped to your door.</p>
        </div>
        <nav aria-label="Footer" className="sheetfoot__links">
          <Link to="/shop">Shop</Link>
          <Link to="/make">Make yours</Link>
          <Link to="/account">Orders &amp; designs</Link>
          <Link to="/help">Shipping &amp; returns</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/cookies">Cookies</Link>
          <Link to="/legal">Legal notice</Link>
          {cfg.data && <a href={`mailto:${cfg.data.supportEmail}`}>{cfg.data.supportEmail}</a>}
        </nav>
        <p className="sheetfoot__small">
          Payments by PayPal. Printed by our print-on-demand partners. ·{" "}
          <button type="button" className="linkbtn" onClick={reopenConsent}>
            Cookie settings
          </button>
        </p>
      </div>
    </footer>
  );
}

export function Toasts() {
  const list = toasts.use((s) => s.list);
  return (
    <div className="toasts" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className="toast" data-tone={t.tone}>
          <span>{t.message}</span>
          {t.action && (
            <Link to={t.action.href} className="btn btn--sm btn--white">
              {t.action.label}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

export function StoreShell() {
  const { pathname } = useLocation();
  const cfg = useConfig();
  useEffect(() => {
    if (cfg.data) initPixel(cfg.data.metaPixelId, cfg.data.currency);
  }, [cfg.data]);
  // The pixel counts the first page itself when it loads; count the rest here.
  const firstPage = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on every route change by design
  useEffect(() => {
    if (firstPage.current) firstPage.current = false;
    else pixel.pageView();
  }, [pathname]);
  // Move focus to the new page on navigation so screen readers announce it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on every route change by design
  useEffect(() => {
    document.getElementById("main")?.focus({ preventScroll: true });
  }, [pathname]);
  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <TestModeBanner />
      <Header />
      <main id="main" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
      <Toasts />
      <CookieBanner />
      <ConfirmHost />
      <ScrollRestoration />
    </>
  );
}
